import { IconButton } from '@/components/shared/IconButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { RESOURCE_TYPE_META } from '@/lib/constants/resourceTypes';
import { avatarColorClass, getCompanyInitials } from '@/lib/utils/avatar';
import { relativeTime } from '@/lib/utils/formatters';
import {
  getActionLink,
  getDescriptionSegments,
} from '@/lib/utils/notificationHelpers';
import type { NotificationItemProps } from './types';

export function NotificationItem({
  notification: n,
  tab,
  onDismiss,
  onReactivate,
}: NotificationItemProps) {
  const { getTranslation: t } = useTranslator({ page: 'notifications' });
  const isActivity = tab === 'activity';
  const isDismissed = n.status === 'DISMISSED';
  const isUnread = n.status === 'UNREAD' || n.status === 'PENDING';
  const actionLink = isActivity ? getActionLink(n, t) : null;
  const segments = getDescriptionSegments(n, t);
  const resourceTypeMeta = RESOURCE_TYPE_META[n.resourceType];

  return (
    <div
      className={`flex items-start gap-2 px-6 py-3 transition-colors hover:bg-brand-accent/10 ${
        isUnread ? 'bg-primary/2' : ''
      }`}
    >
      {/* Unread dot — left of avatar for notifications tab */}
      {!isActivity && (
        <div className='flex items-start pt-1.5 w-3 shrink-0'>
          {isUnread && <span className='size-2 rounded-full bg-destructive' />}
        </div>
      )}

      {/* Avatar — only shown when there's a company image */}
      {n.createdByCompanyImageUrl && (
        <Avatar className='size-8 shrink-0 rounded-full mt-0.5'>
          <AvatarImage
            src={`${n.createdByCompanyImageUrl}?fit=crop&h=64&w=64`}
            alt={n.createdByCompanyName}
            className='object-cover'
          />
          <AvatarFallback
            className={`rounded-full text-xs font-bold ${avatarColorClass(n.createdByCompanyId)}`}
          >
            {getCompanyInitials(n.createdByCompanyName)}
          </AvatarFallback>
        </Avatar>
      )}

      {/* Content */}
      <div className='flex-1 min-w-0'>
        <p className='text-xs leading-snug text-foreground line-clamp-2'>
          {segments.map((seg, i) =>
            seg.bold ? (
              <strong key={i}>{seg.text}</strong>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </p>

        {/* Badge + time */}
        <div className='flex items-center gap-2 mt-1 flex-wrap'>
          {resourceTypeMeta && (
            <span
              className='text-xs font-medium px-1.5 py-0.5 rounded text-foreground'
              style={{ backgroundColor: `var(${resourceTypeMeta.cssVar})` }}
            >
              {t(resourceTypeMeta.key)}
            </span>
          )}
          <span className='text-xs text-muted-foreground'>
            {relativeTime(n.createdAt, t)}
          </span>
        </div>
      </div>

      {/* Right: action button + dismiss/reactivate (activity only) */}
      {isActivity && (
        <div className='flex items-center gap-1.5 shrink-0 mt-0.5'>
          {!isDismissed && actionLink?.link && (
            <a
              href={actionLink.link}
              target='_self'
              className='inline-flex items-center h-7 px-2.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/80 transition-colors whitespace-nowrap'
            >
              {actionLink.label}
            </a>
          )}
          {isDismissed && (
            <Button
              size='default'
              variant='outline'
              onClick={() => onReactivate(n.id)}
            >
              {t('actions.reactivate')}
            </Button>
          )}
          {!isDismissed && (
            <IconButton
              variant='ghost'
              size='icon'
              className='size-6 text-muted-foreground hover:text-destructive shrink-0'
              tooltip={t('actions.dismiss')}
              onClick={() => onDismiss(n.id)}
            >
              <i className='fa-light fa-trash text-xs' />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
}
